import nxModule from 'nxModule';

import templateUrl from './batch-loan-preterminations-upload.template.html';
import {Columns} from "../../common/dynamic-list/dynamic-list.component";

import './batch-loan-preterminations-upload.style.less';
import {ActionCategory} from "../../administration/transactions/action-category.types";
import {Unit} from "../../general-ledger/common/gl-category-input.component";
import systemPropertyService from "../../../../react/system/systemPropertyService";
import {HttpService} from "shared/utils/httpService";
import Authentication from "shared/utils/authentication";
import {OfficialReceipt} from "components/dashboard/miscellaneous-transactions/official-receipt/official-receipt.types";
import {CustomerCache} from "components/service/customer.cache.types";
import {NxRouteService} from "routes/NxRouteService";
import {CommandService} from "shared/utils/command/command.types";
import Popup from "shared/common/popup";
import {IFormController} from "angular";

// The CSV holds loan numbers only; amounts are computed server-side.
type PreterminateLoanByBatchInput = {
  categoryId: number,
  officialReceipt: string,
  remarks: string,
  attachedFileId: number
}

interface PreterminateLoanByBatchOutput {
  batchFileId: number;
  rows: PreterminationRow[];
  formatErrors: FormatErrors;
}

interface PreterminateLoanByBatchView extends PreterminateLoanByBatchOutput {
  perRowFormatErrors: RowFormatError[],
  successfulColumns: Columns,
  perRowSuccessfulRows: PreterminationRow[],
  businessErrorColumns: Columns,
  perRowBusinessErrors: PreterminationRowError[]
}

interface RowFormatError {
  lineNo: number ;
  errors: string;
}

interface FormatErrors {
  [key: number]: string[];
}

interface PreterminationRow {
  row: Row;
  officialReceipt?: string;
  errors: string[];
}

interface PreterminationRowError extends PreterminationRow {
  errorText: string
}

interface Row {
  lineNo: number;
  productNumber: string;
}


export class BatchLoanPreterminationsUpload {
  private preterminate: Partial<PreterminateLoanByBatchInput>;
  private simulation: PreterminateLoanByBatchView | null;
  private autoOfficialReceiptEnabled!: boolean;
  private officialReceiptStart?: string;
  skipSimulation = false;

  private readonly formatErrorColumns: Columns = [
    { title: 'Line No', field: 'lineNo'},
    { title: 'Errors', field: 'errors'}
  ];

  // No amount column: the pre-termination command output carries no per-row amounts.
  private readonly successfulColumns: Columns = [
    { title: 'Line No', field: ['row', 'lineNo']},
    { title: 'Invoice', field: 'officialReceipt'},
    { title: 'Product Number', field: ['row', 'productNumber']},
  ];

  private readonly errorTextColumns: Columns = [
    { title: 'Errors', field: 'errorText'}
  ];

  category?: ActionCategory;
  categoryUnits: Unit[] = [];
  form!: IFormController;

  constructor(
    private command: CommandService,
    private popup: Popup,
    private $route: NxRouteService,
    private http: HttpService,
    private authentication: Authentication,
    private customerCache: CustomerCache
  ) {
    this.preterminate = {};
    this.simulation = null;
  }

  async $onInit(): Promise<void> {
    this.autoOfficialReceiptEnabled = systemPropertyService.getProperty('AUTOMATED_OFFICIAL_RECEIPTS_ENABLED') === 'TRUE';
    await this.fetchFirstUnusedOfficialReceipt();
  }

  async fetchFirstUnusedOfficialReceipt(): Promise<void> {
    if (!this.autoOfficialReceiptEnabled) {
      return;
    }

    const userId = this.authentication.context.id;
    const firstUnusedOr = <OfficialReceipt> await this.http.get(`/official-receipt/next-available?userId=${userId}`).toPromise();
    this.officialReceiptStart = firstUnusedOr.receiptNumber;
  }

  async resetForm(): Promise<void> {
    this.clearSimulation();
    this.preterminate = {};
    this.category = undefined;
    this.categoryUnits = [];
    this.skipSimulation = false;
    // nested forms keep their own $dirty state, which gates their validation warnings
    this.form?.$setPristine();
    this.form?.$setUntouched();
    await this.fetchFirstUnusedOfficialReceipt();
  }

  downloadSampleFile(): void {
    const sampleFile = new Blob(['Loan_No\n111-PRD-111111-1'], {type: 'text/csv'});
    const sampleFileUrl = window.URL.createObjectURL(sampleFile);
    const a = document.createElement('a');
    a.href = sampleFileUrl;
    a.download = 'SampleFile.csv';
    a.click();
  }

  clearSimulation(): void {
    this.simulation = null;
  }

  async simulatePretermination(): Promise<void> {
    const { output } = await this.command.execute<unknown, PreterminateLoanByBatchOutput>('SimulatePreterminateLoanByBatch', this.preterminate).toPromise();

    const businessErrorColumns: Columns = [...this.successfulColumns, ...this.errorTextColumns];

    this.simulation = {
      perRowFormatErrors: this.prepareFormatErrors(output),
      perRowBusinessErrors: this.preparePerRowBusinessErrors(output),
      businessErrorColumns,
      perRowSuccessfulRows: this.prepareSuccessfulRows(output),
      successfulColumns: this.successfulColumns,
      ...output
    };
  }

  private prepareSuccessfulRows(output: PreterminateLoanByBatchOutput): PreterminationRow[] {
    return output.rows.filter(row => row.errors.length === 0);
  }

  private preparePerRowBusinessErrors(output: PreterminateLoanByBatchOutput): PreterminationRowError[] {
    return output.rows
        .filter(row => row.errors.length > 0)
        .map(row => ({
          errorText: row.errors.join(', '),
          ...row,
        }));
  }

  private prepareFormatErrors(output: PreterminateLoanByBatchOutput): RowFormatError[] {
    return Object.keys(output.formatErrors) //  lineNo -> listOf errors
        .sort((a, b) => parseInt(a) - parseInt(b)) // sort by line number
        .map((rowNumber: string): RowFormatError => ({
          lineNo: parseInt(rowNumber),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errors: output.formatErrors[rowNumber].join(', ')
        }));
  }

  async submitPretermination(): Promise<void> {
    const response = await this.command.execute<unknown, PreterminateLoanByBatchOutput>('PreterminateLoanByBatch', this.preterminate).toPromise();
    if (!response.approvalRequired && this.customerCache.loadedCustomerId) {
      this.customerCache.loans(this.customerCache.loadedCustomerId).refetch();
    }
    const rowsWithErrors = response.output.rows.filter(r => r.errors?.length > 0);
    if (rowsWithErrors.length > 0) {
      this.popup({text: `Processing skipped ${rowsWithErrors.length} rows with errors. Proceed to history page to view them.`});
    }
    this.$route.reload();
  }

  categoryChanged = (): void => {
    this.categoryUnits = (this.category ? this.category.ledgerAccountFullCodes : [])
      .map(fullCode => ({
        fullCode,
        amount: 0
      }));

    if(this.category) {
      this.preterminate.categoryId = this.category.id;
    }

    this.clearSimulation();
  };
}

nxModule.component('batchLoanPreterminationsUpload', {
  templateUrl,
  controller: BatchLoanPreterminationsUpload,
});
